import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Fade,
  Box,
  Grid,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Person } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { local_url } from "../Const"; // Assuming Const.js holds your local URL

// --- IMPORTANT ---
// Replace these with the actual paths to your assets
import illustration from "../../Assets/loginimage.jpg"; 
import logo from "../../Assets/Logo.png";

const local_URL = `${local_url}`;

export const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSaveCredentials = async (userId, userName, userRole, token, userDetails) => {
    localStorage.setItem("AuthorizationToken", token);
    localStorage.setItem("userId", userId);
    localStorage.setItem("userName", userName);
    localStorage.setItem("userRole", userRole);
    localStorage.setItem("userDetails", JSON.stringify(userDetails));

    // Save userData with user object (for Dashboard compatibility)
    const userData = { token, user: userDetails };
    localStorage.setItem("userData", JSON.stringify(userData));

    // Save areaId and branchId for SGMGR and AGMGR users
    if (userDetails.areaId) {
      localStorage.setItem("areaId", userDetails.areaId);
    }
    if (userDetails.branchId) {
      localStorage.setItem("branchId", userDetails.branchId);
    }
  };

  const handleLogin = async () => {
    setLoading(true);

    if (!username.trim() || !password.trim()) {
      toast.error("Please enter both username and password");
      setLoading(false);
      return;
    }

    try {
      const requestData = {
        userId: username,
        password,
        email: "email", // This seems static, ensure it's correct for your API
      };
      const response = await axios.post(
        `${local_URL}/api/Login/Post`,
        requestData
      );

      if (
        response.status === 200 &&
        response.data?.token &&
        response.data?.user
      ) {
        const { token } = response.data;
        const { userId, userRole, userName } = response.data.user;

        if (!userId || !userName || !userRole) {
          toast.error("Invalid user data received from server.");
          setLoading(false);
          return;
        }

        toast.success("Login successful!");
        await handleSaveCredentials(userId, userName, userRole, token, response.data.user);

        // Navigate SGMGR and AGMGR users directly to Dashboard
        if (userRole === "SGMGR" || userRole === "AGMGR") {
          navigate("/Dashboard", {
            state: { userId, userName, userRole, userDetails: response.data.user },
          });
        } else {
          navigate("/Home", {
            state: { userId, userName, userRole, userDetails: response.data.user },
          });
        }
      } else {
        toast.error("Invalid credentials or unexpected response.");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Invalid credentials");
      } else {
        console.error("Login Error:", error); // Log the full error for debugging
        toast.error("Internal server error. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={800}>
      <Box sx={{ height: "100vh", overflow: "hidden" }}>
        <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
        <Grid container sx={{ height: "100%" }}>
          {/* Left Side: Welcome & Illustration */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "linear-gradient(135deg, #004d40 0%, #00796b 100%)",
              display: { xs: "none", md: "flex" }, // Hide on mobile, show on medium screens up
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              color: "white",
              textAlign: "center",
            }}
          >
            <img
              src={illustration}
              alt="Workplace Illustration"
              style={{ maxWidth: "80%", height: "auto", marginBottom: "2rem" }}
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Welcome to MIS Portal
            </Typography>
            <Typography variant="subtitle1">
              Manage your operations, reports, and data in one secure place.
            </Typography>
            
          </Grid>

          {/* Right Side: Login Form */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 2, sm: 3 },
              backgroundColor: "#f5f5f5",
            }}
          >
            <Container maxWidth="sm">
              <Card
                elevation={6}
                sx={{
                  borderRadius: 4,
                  boxShadow: "0px 10px 25px rgba(0,0,0,0.1)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  {/* Logo for mobile view */}
                  <Box sx={{ textAlign: "center", mb: 3, display: { md: 'none' } }}>
                     <img
                        src={illustration}
                        alt="Workplace Illustration"
                        style={{ maxWidth: "60%", height: "auto"}}
                      />
                  </Box>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <img
                      src={logo}
                      alt="MIS Portal Logo"
                      style={{ width: "240px", height: "auto" }}
                    />
                  </Box>

                  <Typography
                    variant="h5"
                    align="center"
                    gutterBottom
                    fontWeight="bold"
                    color="text.primary"
                  >
                    MIS Portal Login
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={3} component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                    <TextField
                      label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      onClick={handleLogin}
                      disabled={loading}
                      fullWidth
                      sx={{
                        bgcolor: "#00796b",
                        color: "white",
                        fontSize: "1rem",
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": { bgcolor: "#004d40" },
                        transition: 'background-color 0.3s ease-in-out',
                      }}
                      endIcon={
                        loading ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : null
                      }
                    >
                      {loading ? "Logging in..." : "Login"}
                    </Button>
                  </Stack>

                  <Typography
                    variant="caption"
                    display="block"
                    align="center"
                    sx={{ mt: 4, color: "text.secondary" }}
                  >
                    © {new Date().getFullYear()} IB Group • MIS Portal v1.1
                  </Typography>
                </CardContent>
              </Card>
            </Container>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default Login;